import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./ServicesPage.css";
import { useLocalContext } from "../../context/LocalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faBlog,
  faCloud,
  faGlobe,
  faShieldHalved,
  faEnvelope,
  faArrowLeft,
  faArrowRight,
  faPhone,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

const iconMap = {
  "shared-hosting": faServer,
  "wordpress-hosting": faBlog,
  "vps-hosting": faCloud,
  "domain-registration": faGlobe,
  "ssl-security": faShieldHalved,
  "email-hosting": faEnvelope,
};

const ServicesPage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const { webinfo = {} } = useLocalContext();

  const brandName = webinfo?.name || "Our team";

  const serviceData = useMemo(
    () => [
      {
        id: "shared-hosting",
        kicker: "Shared",
        title: "Shared & cPanel hosting",
        description:
          "Reliable Linux hosting with SSD storage, easy control-panel access, and room to grow — perfect for brochure sites, blogs, and small shops.",
        details:
          "Shared hosting is where most sites start: your files live on enterprise-grade hardware alongside careful resource limits so performance stays predictable. We help you pick the right tier based on traffic, plugins, and email needs — and we’re here when it’s time to scale up to VPS.",
        features: [
          "SSD-accelerated storage and modern PHP versions.",
          "cPanel-style management: files, databases, cron, backups.",
          "One-click installers for common apps (where supported).",
          "Free SSL certificate options for HTTPS.",
          "Email mailboxes on your domain (plan-dependent).",
          "Guidance on caching, .htaccess, and performance basics.",
        ],
        outcomes: ["Predictable cost", "Easy day-to-day management", "Room to grow"],
        testimonials: [
          {
            name: "Neha R.",
            feedback:
              "Migration was smooth and DNS finally made sense. Support answered the same day.",
          },
          {
            name: "Marcus T.",
            feedback:
              "Our brochure site loads quickly and the panel is straightforward for our team.",
          },
        ],
        faqs: [
          {
            question: "Is shared hosting enough for an online store?",
            answer:
              "Often yes for small catalogs. For heavy traffic or large product feeds, we may recommend a higher tier or VPS.",
          },
          {
            question: "Can I upgrade later without downtime drama?",
            answer:
              "Yes. We plan upgrades with you and can move you to WordPress-optimized or VPS plans when you’re ready.",
          },
          {
            question: "Do you help move my site from another host?",
            answer:
              "Yes — we offer migration assistance including files, databases, and DNS cutover planning.",
          },
        ],
      },

      {
        id: "wordpress-hosting",
        kicker: "WordPress",
        title: "Managed WordPress hosting",
        description:
          "A stack tuned for WordPress — with sensible caching defaults, security hygiene, and support that speaks your language.",
        details:
          "WordPress has specific needs: PHP versions, database performance, plugin conflicts, and caching behavior. We optimize the environment for typical WordPress workloads, help you avoid common pitfalls, and advise when it’s time to add a CDN or move to more resources.",
        features: [
          "PHP versions aligned with WordPress requirements.",
          "Database tuning and backup integration.",
          "Staging / clone guidance (where available on plan).",
          "Security: SSL, login hardening tips, update strategy.",
          "Performance: object cache and theme/plugin guidance.",
          "Migration from other WordPress hosts.",
        ],
        outcomes: ["Faster perceived load", "Fewer plugin surprises", "Clear upgrade path"],
        testimonials: [
          {
            name: "Priya S.",
            feedback:
              "They spotted a bloated plugin before it took the site down. Response time has been great.",
          },
          {
            name: "Daniel K.",
            feedback:
              "Finally a host that explains caching without jargon. Our editors are happier.",
          },
        ],
        faqs: [
          {
            question: "Do you allow any plugin?",
            answer:
              "Most yes. We flag plugins known to cause performance or security issues and suggest alternatives.",
          },
          {
            question: "Can you help with WooCommerce?",
            answer:
              "Yes — we’ll size the plan for cart traffic, cron, and checkout performance.",
          },
          {
            question: "What about multisite?",
            answer:
              "Supported on appropriate plans; tell us your network structure and we’ll confirm fit.",
          },
        ],
      },

      {
        id: "vps-hosting",
        kicker: "VPS",
        title: "VPS & cloud servers",
        description:
          "Dedicated CPU, RAM, and disk for apps, APIs, and busy sites — with root access when you need full control.",
        details:
          "When shared hosting isn’t enough, a VPS gives you isolated resources and the ability to tune the stack. Ideal for custom Node or PHP apps, multiple sites with strict requirements, or predictable performance under load. We help you choose OS, panel (or no panel), and backup strategy.",
        features: [
          "Choice of resources: CPU, RAM, NVMe/SSD storage.",
          "Root access and SSH for advanced users.",
          "Firewall and SSH key best practices.",
          "Optional control panels or we help with bare-metal style setups.",
          "Snapshot / backup options depending on platform.",
          "Scaling guidance as traffic grows.",
        ],
        outcomes: ["Isolated resources", "Full stack control", "Predictable scaling"],
        testimonials: [
          {
            name: "Alex M.",
            feedback:
              "We moved our API to VPS here and latency to our users dropped noticeably.",
          },
          {
            name: "Sofia L.",
            feedback:
              "Support helped harden SSH and set up automated backups — huge relief.",
          },
        ],
        faqs: [
          {
            question: "Do I need Linux experience?",
            answer:
              "Helpful but not mandatory — we can manage more for you or co-manage with your developers.",
          },
          {
            question: "Can I run Docker?",
            answer:
              "Often yes on suitable images; tell us your workload and we’ll confirm compatibility.",
          },
          {
            question: "How is billing handled?",
            answer:
              "Typically monthly or annual per resource tier — we’ll quote based on your needs.",
          },
        ],
      },

      {
        id: "domain-registration",
        kicker: "Domains",
        title: "Domains & DNS",
        description:
          "Register, transfer, and point your domains with confidence — including DNS records, subdomains, and email routing.",
        details:
          "Your domain is your address on the internet. We help you secure the right name, transfer from other registrars without downtime surprises, and configure A, AAAA, CNAME, MX, and TXT records for hosting, SSL validation, and third-party services.",
        features: [
          "Registration and transfer assistance.",
          "DNS zone management: A, CNAME, MX, TXT, SRV.",
          "Subdomains for apps, staging, or regional sites.",
          "TTL guidance for safe changes.",
          "Integration with hosting and email products.",
          "WHOIS privacy where applicable.",
        ],
        outcomes: ["Clean DNS", "Safer transfers", "Less email deliverability guesswork"],
        testimonials: [
          {
            name: "Jordan P.",
            feedback:
              "They walked us through a registrar transfer without our email going dark.",
          },
          {
            name: "Elena V.",
            feedback:
              "SPF and DKIM were set up properly — our newsletters finally land in inboxes.",
          },
        ],
        faqs: [
          {
            question: "How long does a transfer take?",
            answer:
              "Often 5–7 days depending on the losing registrar; we give you a checklist to avoid expiry issues.",
          },
          {
            question: "Can I use Cloudflare with you?",
            answer:
              "Yes — we’ll align origin IPs and SSL modes with your hosting setup.",
          },
          {
            question: "Do you sell all TLDs?",
            answer:
              "We support many popular TLDs; ask for anything specific.",
          },
        ],
      },

      {
        id: "ssl-security",
        kicker: "Security",
        title: "SSL & website security",
        description:
          "Encrypt traffic with SSL/TLS and add layers of protection — from free certificates to advanced scanning and hardening advice.",
        details:
          "HTTPS is expected by browsers and search engines. We help you issue and renew certificates, fix mixed content, and tune cipher suites. For higher-risk sites, we discuss WAF options, malware scanning, and access controls.",
        features: [
          "Let’s Encrypt and commercial SSL options.",
          "Mixed-content troubleshooting after HTTPS migration.",
          "Firewall-friendly hosting configurations.",
          "Optional malware scanning and cleanup partners.",
          "HSTS and redirect best practices.",
          "Security headers guidance (CSP, etc.) for developers.",
        ],
        outcomes: ["Trusted browser padlock", "Better SEO hygiene", "Reduced risk"],
        testimonials: [
          {
            name: "Chris W.",
            feedback:
              "Auto-renew SSL broke after a domain change — they fixed chain issues in one session.",
          },
          {
            name: "Amira H.",
            feedback:
              "Clear explanation of what scanning does and doesn’t catch. No fear-mongering.",
          },
        ],
        faqs: [
          {
            question: "Is free SSL enough?",
            answer:
              "For most sites, yes. EV or wildcard certs are for specific enterprise or multi-subdomain needs.",
          },
          {
            question: "Do you offer a WAF?",
            answer:
              "We can recommend CDN/WAF combos that fit your stack and budget.",
          },
          {
            question: "What if my site was hacked?",
            answer:
              "We help isolate, restore from backup, and patch vulnerable themes/plugins or apps.",
          },
        ],
      },

      {
        id: "email-hosting",
        kicker: "Email",
        title: "Professional email hosting",
        description:
          "Email at yourdomain.com with webmail, forwarding, and spam filtering — aligned with your hosting and DNS.",
        details:
          "Customers trust a branded inbox. We set up mailboxes or forwarding, configure SPF, DKIM, and DMARC for deliverability, and help you connect phones and desktop clients. If you outgrow basic mail, we discuss Google Workspace or Microsoft 365 migration paths.",
        features: [
          "Mailboxes and aliases on your domain.",
          "Webmail and IMAP/SMTP for clients.",
          "Spam and virus filtering (platform-dependent).",
          "SPF, DKIM, DMARC setup assistance.",
          "Forwarding and catch-all options where supported.",
          "Guidance for bulk sending vs transactional email.",
        ],
        outcomes: ["Professional presence", "Better deliverability", "Clear DNS alignment"],
        testimonials: [
          {
            name: "Tomás G.",
            feedback:
              "Finally stopped landing in spam after DKIM was configured correctly.",
          },
          {
            name: "Rina K.",
            feedback:
              "Our team got shared calendars later; they planned the upgrade path early.",
          },
        ],
        faqs: [
          {
            question: "How many mailboxes can I have?",
            answer:
              "Depends on your hosting tier; we’ll map mailboxes to your plan.",
          },
          {
            question: "Can I use Gmail with my domain?",
            answer:
              "Yes via Google Workspace — we help with MX cutover.",
          },
          {
            question: "Is SMTP port blocking an issue?",
            answer:
              "We explain provider rules and alternatives for apps and newsletters.",
          },
        ],
      },
    ],
    []
  );

  const idx = serviceData.findIndex((s) => s.id === serviceId);
  const service = idx >= 0 ? serviceData[idx] : null;

  const prev = idx > 0 ? serviceData[idx - 1] : null;
  const next = idx >= 0 && idx < serviceData.length - 1 ? serviceData[idx + 1] : null;

  const fallbackPhoneCall = webinfo?.phonecall || "+918000000000";
  const fallbackPhone = webinfo?.phone || "+91 80000 00000";
  const fallbackEmail = webinfo?.email || "contact@digivirtuehost.com";
  const fallbackHours = webinfo?.hours || "Mon–Sat · 10:00–19:00 IST";

  if (!service) {
    return (
      <section className="spx" aria-label="Service not found">
        <div className="spx-wrap">
          <div className="spx-topbar">
            <button className="spx-back" onClick={() => navigate("/services")}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to hosting
            </button>
          </div>

          <header className="spx-hero">
            <p className="spx-kicker">Hosting</p>
            <h1>Page not found</h1>
            <p className="spx-sub">
              We couldn’t find that hosting product. Go back and choose a plan or
              service.
            </p>
          </header>
        </div>
      </section>
    );
  }

  const icon = iconMap[service.id] || faServer;

  return (
    <section className="spx" aria-label="Hosting service details">
      <div className="spx-wrap">
        <div className="spx-topbar">
          <button className="spx-back" onClick={() => navigate("/services")}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back to hosting
          </button>

          <div className="spx-nav">
            <button
              className="spx-navbtn"
              disabled={!prev}
              onClick={() => prev && navigate(`/services/${prev.id}`)}
              aria-label="Previous service"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Prev</span>
            </button>

            <button
              className="spx-navbtn"
              disabled={!next}
              onClick={() => next && navigate(`/services/${next.id}`)}
              aria-label="Next service"
            >
              <span>Next</span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        </div>

        <header className="spx-hero" aria-labelledby="service-title">
          <div className="spx-badge">
            <span className="spx-badgeIcon" aria-hidden="true">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span className="spx-badgeText">{service.kicker}</span>
          </div>

          <h1 id="service-title">
            {service.title}
            <span className="spx-grad"> built for reliability.</span>
          </h1>

          <p className="spx-sub">{service.description}</p>

          <div className="spx-heroRow" aria-hidden="true">
            {service.outcomes?.slice(0, 3).map((o) => (
              <span className="spx-pill" key={o}>
                {o}
              </span>
            ))}
          </div>

          <div className="spx-ctaRow">
            <a className="spx-btn spx-btnPrimary" href="/contact">
              Get a hosting quote
            </a>
            <button className="spx-btn spx-btnGhost" onClick={() => navigate("/services")}>
              All hosting products
            </button>
          </div>
        </header>

        <div className="spx-grid">
          <div className="spx-left">
            <article className="spx-card">
              <h2>How {brandName} helps</h2>
              <p>{service.details}</p>
            </article>

            <article className="spx-card">
              <h2>What’s included</h2>
              <div className="spx-features">
                {service.features.map((f, i) => (
                  <div className="spx-feature" key={i}>
                    <span className="spx-tick" aria-hidden="true" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </article>

            {service.faqs?.length > 0 && (
              <article className="spx-card spx-faqs">
                <h2>Common questions</h2>
                {service.faqs.map((qa, i) => (
                  <details key={i}>
                    <summary>{qa.question}</summary>
                    <p>{qa.answer}</p>
                  </details>
                ))}
              </article>
            )}
          </div>

          <aside className="spx-right" aria-label="Contact">
            <div className="spx-side">
              <p className="spx-sideKicker">Talk to us</p>
              <h3>Questions about this product?</h3>
              <p className="spx-sideCopy">
                Tell us your traffic, stack, and timeline. We’ll suggest the right
                tier and help plan DNS, SSL, and migration.
              </p>

              <div className="spx-meta">
                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faPhone} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Call</div>
                    <a className="spx-metaValue" href={`tel:${fallbackPhoneCall}`}>
                      {fallbackPhone}
                    </a>
                  </div>
                </div>

                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Email</div>
                    <a className="spx-metaValue" href={`mailto:${fallbackEmail}`}>
                      {fallbackEmail}
                    </a>
                  </div>
                </div>

                <div className="spx-metaRow">
                  <span className="spx-metaIcon" aria-hidden="true">
                    <FontAwesomeIcon icon={faClock} />
                  </span>
                  <div>
                    <div className="spx-metaLabel">Hours</div>
                    <div className="spx-metaValue">{fallbackHours}</div>
                  </div>
                </div>
              </div>

              <div className="spx-sidePills" aria-hidden="true">
                <span>Fast response</span>
                <span>Clear specs</span>
                <span>Migration help</span>
                <span>No jargon</span>
              </div>

              <button
                className="spx-callBtn"
                onClick={() => (window.location.href = `tel:${fallbackPhoneCall}`)}
              >
                Call now
              </button>
            </div>
          </aside>
        </div>

        {service.testimonials?.length ? (
          <div className="spx-testimonials" aria-label="Testimonials">
            <h2>What customers say</h2>
            <div className="spx-tgrid">
              {service.testimonials.map((t, i) => (
                <div className="spx-quote" key={i}>
                  <p>“{t.feedback}”</p>
                  <span>— {t.name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ServicesPage;
